import React, { createContext, useContext, useMemo, useState } from 'react';

const OnboardingContext = createContext(null);

const defaultProfileDraft = {
  full_name: '',
  gender: '',
  date_of_birth: '',
  height_cm: '',
  weight_kg: '',
  activity_level: '',
  fitness_goal: '',
  email: '',
};

const defaultFlags = {
  apple_health_connected: null,
  fitness_test_pending: null,
};

export function OnboardingProvider({ children, totalSteps }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [authUser, setAuthUser] = useState(null);
  const [profileDraft, setProfileDraft] = useState(defaultProfileDraft);
  const [flags, setFlags] = useState(defaultFlags);
  const [completedSteps, setCompletedSteps] = useState({});
  const [loading, setLoading] = useState(false);

  const value = useMemo(
    () => ({
      currentStep,
      setCurrentStep,
      nextStep: () => setCurrentStep((prev) => Math.min(prev + 1, (totalSteps ?? 1) - 1)),
      prevStep: () => setCurrentStep((prev) => Math.max(prev - 1, 0)),
      totalSteps: totalSteps ?? 1,
      authUser,
      setAuthUser,
      profileDraft,
      setProfileDraft: (updates) =>
        setProfileDraft((prev) => ({
          ...prev,
          ...updates,
        })),
      resetProfileDraft: () => setProfileDraft(defaultProfileDraft),
      flags,
      setFlags: (updates) =>
        setFlags((prev) => ({
          ...prev,
          ...updates,
        })),
      completedSteps,
      markStepComplete: (stepId) =>
        setCompletedSteps((prev) => ({
          ...prev,
          [stepId]: true,
        })),
      loading,
      setLoading,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authUser, currentStep, completedSteps, flags, loading, totalSteps, profileDraft]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

export function useOnboarding() {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return ctx;
}
